import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import SearchFilterBar from '../../components/ui/SearchFilterBar';
import QuoteTable from './components/QuoteTable';
import QuoteViewModal from './components/QuoteViewModal';
import DeleteConfirmModal from './components/DeleteConfirmModal';

import Button from '../../components/ui/Button';
import { quoteService } from '../../services/quoteService';
import { useAuth } from '../../contexts/AuthContext';

const QuoteManagement = () => {
  const { user } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [quotes, setQuotes] = useState([]);
  const [filteredQuotes, setFilteredQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Modal states
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  // Load quotes on component mount
  useEffect(() => {
    loadQuotes();
  }, []);

  // Filter quotes when search term or filters change
  useEffect(() => {
    filterQuotes();
  }, [quotes, searchTerm, statusFilter, dateFilter, sortBy, sortOrder]);

  const loadQuotes = async () => {
    try {
      setLoading(true);
      const { quotes: quotesData, error } = await quoteService?.getQuotes();
      
      if (error) {
        setError(error);
        return;
      }
      
      setQuotes(quotesData || []);
      setError(null);
    } catch (error) {
      setError('Failed to load quotes');
    } finally {
      setLoading(false);
    }
  };

  const filterQuotes = () => {
    let filtered = [...quotes];

    // Apply search filter
    if (searchTerm?.trim()) {
      filtered = filtered?.filter(quote => {
        const clientName = `${quote?.client?.first_name} ${quote?.client?.last_name}`?.toLowerCase();
        const quoteNumber = quote?.quote_number?.toLowerCase() || '';
        const term = searchTerm?.toLowerCase();
        
        return clientName?.includes(term) || quoteNumber?.includes(term);
      });
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered?.filter(quote => quote?.status === statusFilter);
    }

    // Apply date filter
    if (dateFilter !== 'all') {
      const now = new Date();
      const filterDate = new Date();
      
      switch (dateFilter) {
        case 'today':
          filterDate?.setHours(0, 0, 0, 0);
          filtered = filtered?.filter(quote => new Date(quote?.created_at) >= filterDate);
          break;
        case 'week':
          filterDate?.setDate(now?.getDate() - 7);
          filtered = filtered?.filter(quote => new Date(quote?.created_at) >= filterDate);
          break;
        case 'month':
          filterDate?.setMonth(now?.getMonth() - 1);
          filtered = filtered?.filter(quote => new Date(quote?.created_at) >= filterDate);
          break;
        case 'year':
          filterDate?.setFullYear(now?.getFullYear() - 1);
          filtered = filtered?.filter(quote => new Date(quote?.created_at) >= filterDate);
          break;
      }
    }

    // Apply sorting
    filtered?.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'quote_number':
          aValue = a?.quote_number?.toLowerCase() || '';
          bValue = b?.quote_number?.toLowerCase() || '';
          break;
        case 'client_name':
          aValue = `${a?.client?.first_name} ${a?.client?.last_name}`?.toLowerCase();
          bValue = `${b?.client?.first_name} ${b?.client?.last_name}`?.toLowerCase();
          break;
        case 'status':
          aValue = a?.status || '';
          bValue = b?.status || '';
          break;
        case 'total_price':
          aValue = a?.total_price || 0;
          bValue = b?.total_price || 0;
          break;
        case 'created_at':
        default:
          aValue = new Date(a?.created_at || 0);
          bValue = new Date(b?.created_at || 0);
          break;
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredQuotes(filtered);
  };

  const handleViewQuote = (quote) => {
    setSelectedQuote(quote);
    setIsViewModalOpen(true);
  };

  const handleDeleteQuote = (quote) => {
    setSelectedQuote(quote);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      const { error } = await quoteService?.deleteQuote(selectedQuote?.id);
      
      if (error) {
        throw new Error(error);
      }
      
      await loadQuotes(); // Reload to get updated data
      setIsDeleteModalOpen(false);
      setSelectedQuote(null);
    } catch (error) {
      alert(`Failed to delete quote: ${error?.message}`);
    }
  };

  const handleUpdateQuoteStatus = async (quoteId, newStatus) => {
    try {
      const { quote, error } = await quoteService?.updateQuote(quoteId, { status: newStatus });
      
      if (error) {
        throw new Error(error);
      }
      
      await loadQuotes(); // Reload to get updated data
    } catch (error) {
      alert(`Failed to update quote status: ${error?.message}`);
    }
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const handleFilter = (filters) => {
    setStatusFilter(filters?.status || 'all');
    setDateFilter(filters?.date || 'all');
    setSortBy(filters?.sortBy || 'created_at');
    setSortOrder(filters?.sortOrder || 'desc');
  };

  // Calculate stats
  const stats = {
    total: quotes?.length || 0,
    draft: quotes?.filter(q => q?.status === 'draft')?.length || 0,
    pending: quotes?.filter(q => q?.status === 'pending_review')?.length || 0,
    approved: quotes?.filter(q => q?.status === 'approved')?.length || 0,
    totalValue: quotes?.reduce((sum, q) => sum + (q?.total_price || 0), 0) || 0
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <Sidebar isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} />
        <main className={`transition-all duration-200 ease-out pt-16 pb-20 lg:pb-8 ${
          sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'
        }`}>
          <div className="px-4 lg:px-8 py-6">
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
                <p className="mt-4 text-text-secondary">Chargement des devis...</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <Sidebar isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} />
        <main className={`transition-all duration-200 ease-out pt-16 pb-20 lg:pb-8 ${
          sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'
        }`}>
          <div className="px-4 lg:px-8 py-6">
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <p className="text-red-600 mb-4">{error}</p>
                <Button onClick={loadQuotes} variant="primary">
                  Réessayer
                </Button>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar 
        isCollapsed={sidebarCollapsed} 
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} 
      />
      <main className={`transition-all duration-200 ease-out pt-16 pb-20 lg:pb-8 ${
        sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'
      }`}>
        <div className="px-4 lg:px-8 py-6">
          <div className="max-w-7xl mx-auto">
            <Breadcrumbs />
            
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-2xl lg:text-3xl font-bold text-text-primary">
                  Gestion des Devis
                </h1>
                <p className="text-text-secondary mt-1">
                  Consultez et gérez tous vos devis
                </p>
              </div>
              
              <div className="flex items-center space-x-3">
                <Button
                  variant="primary"
                  iconName="Plus"
                  iconPosition="left"
                  onClick={() => window.location.href = '/quote-calculator'}
                  className="hidden sm:flex"
                >
                  Nouveau Devis
                </Button>
                
                <Button
                  variant="primary"
                  iconName="Plus"
                  onClick={() => window.location.href = '/quote-calculator'}
                  className="sm:hidden"
                />
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="bg-card rounded-lg border border-border p-6">
                <div className="flex items-center">
                  <div className="p-2 rounded-md bg-blue-50">
                    <div className="w-4 h-4 bg-blue-500 rounded"></div>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-text-secondary">Total Devis</p>
                    <p className="text-2xl font-bold text-text-primary">{stats?.total}</p>
                  </div>
                </div>
              </div>

              <div className="bg-card rounded-lg border border-border p-6">
                <div className="flex items-center">
                  <div className="p-2 rounded-md bg-yellow-50">
                    <div className="w-4 h-4 bg-yellow-500 rounded"></div>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-text-secondary">En Attente</p>
                    <p className="text-2xl font-bold text-text-primary">{stats?.pending}</p>
                  </div>
                </div>
              </div>

              <div className="bg-card rounded-lg border border-border p-6">
                <div className="flex items-center">
                  <div className="p-2 rounded-md bg-green-50">
                    <div className="w-4 h-4 bg-green-500 rounded"></div>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-text-secondary">Approuvés</p>
                    <p className="text-2xl font-bold text-text-primary">{stats?.approved}</p>
                  </div>
                </div>
              </div>

              <div className="bg-card rounded-lg border border-border p-6">
                <div className="flex items-center">
                  <div className="p-2 rounded-md bg-purple-50">
                    <div className="w-4 h-4 bg-purple-500 rounded"></div>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-text-secondary">Valeur Totale</p>
                    <p className="text-2xl font-bold text-text-primary">
                      {stats?.totalValue?.toLocaleString('fr-FR', { 
                        style: 'currency', 
                        currency: 'EUR' 
                      })}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-card rounded-lg border border-border shadow-sm">
              <div className="p-6 border-b border-border">
                <SearchFilterBar
                  onSearch={handleSearch}
                  onFilter={handleFilter}
                  searchPlaceholder="Rechercher par numéro ou client..."
                  filters={[
                    {
                      key: 'status',
                      label: 'Statut',
                      options: [
                        { value: 'all', label: 'Tous les statuts' },
                        { value: 'draft', label: 'Brouillon' },
                        { value: 'pending_review', label: 'En révision' },
                        { value: 'approved', label: 'Approuvé' },
                        { value: 'rejected', label: 'Rejeté' },
                        { value: 'expired', label: 'Expiré' }
                      ]
                    },
                    {
                      key: 'date',
                      label: 'Période',
                      options: [
                        { value: 'all', label: 'Toutes les périodes' },
                        { value: 'today', label: 'Aujourd\'hui' },
                        { value: 'week', label: 'Cette semaine' },
                        { value: 'month', label: 'Ce mois' },
                        { value: 'year', label: 'Cette année' }
                      ]
                    },
                    {
                      key: 'sortBy',
                      label: 'Trier par',
                      options: [
                        { value: 'created_at', label: 'Date de création' },
                        { value: 'quote_number', label: 'Numéro de devis' },
                        { value: 'client_name', label: 'Nom du client' },
                        { value: 'total_price', label: 'Montant' },
                        { value: 'status', label: 'Statut' }
                      ]
                    }
                  ]}
                />
              </div>

              <QuoteTable
                quotes={filteredQuotes}
                onViewQuote={handleViewQuote}
                onDeleteQuote={handleDeleteQuote}
                onUpdateStatus={handleUpdateQuoteStatus}
                onResendEmail={() => {}}
                onDuplicateQuote={() => {}}
                loading={false}
              />
            </div>
          </div>
        </div>
      </main>
      {/* Modals */}
      <QuoteViewModal
        isOpen={isViewModalOpen}
        onClose={() => {
          setIsViewModalOpen(false);
          setSelectedQuote(null);
        }}
        quote={selectedQuote}
        onResendEmail={() => {}}
        onDuplicate={() => {}}
      />
      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedQuote(null);
        }}
        onConfirm={handleConfirmDelete}
        quoteName={selectedQuote?.quote_number}
      />
    </div>
  );
};

export default QuoteManagement;